import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../../../config';
import { FormHelpers, OnFormInitialValuesChange } from '../../../../../screens/utils';
import { journeyDataActions } from '../../../../../services/redux/features/journeyData.slice';
import { createFormInputs } from '../../../../ApplicationFormInputs/applicationFormInputs';
import { FormSectionEditButton } from '../../../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../../../FormSectionSummaryTitle';
import { DriverDetailsFormModel_OB_FP } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
};

/**
 * - OtherBusiness (LTD, PLC, Other, Unlimited, Partnership)
 * - FinancialProduct
 */
export const DriverDetailsForm_OB_FP: React.FC<Props> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema] = useState<ObjectSchema<Partial<DriverDetailsFormModel_OB_FP>>>(getValidationSchema);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (/* values: ThisFormModel */) => {
    /* start loader */
    Promise.resolve(/* submit */).then(onFormSubmitSuccess).catch(/* on error */).finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`driverDetailsForm:title:business`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent onFormInitialValuesChange={onFormInitialValuesChange} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  formHelpers: FormHelpers;
  onFormInitialValuesChange: OnFormInitialValuesChange;
};

export const FormContent = ({ formHelpers }: FormContentProps) => {
  const [inputs] = useState(() => createFormInputs('companyDetailsForm_OL'));
  const dispatch = useDispatch();
  const { values, submitForm } = useFormikContext<DriverDetailsFormModel_OB_FP>();
  const { isDisabledFormSummary } = formHelpers;

  useEffect(() => {
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const disableForm = isDisabledFormSummary;

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/4" s="1/1">{inputs.firstName()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.lastName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
